import { Box, Chip } from '@mui/material';
import clsx from 'clsx';
import { CHIP_SELECTOR_ID } from 'constants/ids';

import { IOptionType } from 'interfaces/forms';

import { FormField } from 'components/_basic/Form/FormField';

import { IChipSelectorProps } from './types';

import styles from './ChipSelector.module.scss';

export const ChipSelector = <T extends IOptionType>(props: IChipSelectorProps<T>) => {
  const { id, label, fieldError, selected, options, disabled, multiple = false, onChange, chipStyles, name } = props;

  const getArrayOfSelected = () => (Array.isArray(selected) ? selected : [selected]);

  const handleSelect = (option: T) => {
    let newValue: T[];

    const arraySelected = getArrayOfSelected();

    if (multiple) {
      // If multiple is true, add or remove an element from the array.
      if (arraySelected.some((selectedOption) => selectedOption.value === option.value)) {
        newValue = arraySelected.filter((selectedOption) => selectedOption.value !== option.value);
      } else {
        newValue = [...arraySelected, option];
      }
    } else {
      // If multiple is false, we save only one value.
      const isOptionSelected = arraySelected.some((selectedOption) => selectedOption.value === option.value);

      newValue = isOptionSelected
        ? arraySelected.filter((selectedOption) => selectedOption.value !== option.value)
        : [option];
    }

    // if we don't have new selected value then just return already selected
    const changeValue = newValue.length ? newValue : arraySelected;
    onChange(changeValue);
  };

  const isSelected = (option: T) => {
    const arraySelected = getArrayOfSelected();
    return arraySelected.some(({ value }) => value === option.value);
  };

  return (
    <FormField id={id} label={label} error={fieldError?.message} omitErrorClone>
      <Box className={styles.chipsBox}>
        {options.map((option) => (
          <Chip
            clickable
            component="button"
            name={name}
            data-id={CHIP_SELECTOR_ID}
            key={option.value}
            label={option.label}
            onClick={() => handleSelect(option)}
            color={isSelected(option) ? 'primary' : 'default'}
            variant="outlined"
            disabled={disabled}
            className={clsx(chipStyles?.chipStyle, {
              [chipStyles?.selected || '']: isSelected(option),
            })}
          />
        ))}
      </Box>
    </FormField>
  );
};
